/**
 * What one History row says (PRD-0075 SC-003): a source and a change, never the mechanics behind
 * it. A mention reads "Mentioned in <document>", a property change "<key>: <old> → <new>
 * (<document>)", a re-extraction "Earlier extraction of <document> replaced". Any other kind
 * reads as its label (the card's).
 */
import type { TFunction } from 'i18next'
import type { CaseEventKind } from '@/services/api/case-events'

const HISTORY = 'investigations.entityDetail.history'

/** A property change as its row shows it. */
export interface PropertyChangeRow {
  key: string
  /** The value replaced; null when the property had none. */
  old: string | null
  new: string | null
  /** The replaced value came from another source (ADR-0093). */
  conflict: boolean
  /** An analyst's pin kept `new` out: `old` is the asserted value, and stays. */
  pinned?: boolean
  /** Where the replaced value came from, when the history names that document. */
  previousDocumentName: string | null
}

/** What the text of a row needs. */
export interface HistoryRowText {
  kind: CaseEventKind | null
  documentName: string | null
  property?: PropertyChangeRow | null
}

/** The row's own wording; null for a kind that reads as its label. */
export function historyRowText(row: HistoryRowText, t: TFunction): string | null {
  const document = row.documentName ?? t('investigations.entityDetail.unknownDocument')
  if (row.kind === 'entity_mentioned') return t(`${HISTORY}.mentionedIn`, { document })
  if (row.kind === 'source_superseded') return t(`${HISTORY}.rows.superseded`, { document })
  if (row.kind !== 'property_changed' || !row.property) return null
  const none = t(`${HISTORY}.header.noValue`)
  const values = {
    key: row.property.key,
    old: row.property.old ?? none,
    new: row.property.new ?? none,
  }
  const wording = row.property.pinned ? 'propertyPinned' : 'propertyChanged'
  return row.documentName
    ? t(`${HISTORY}.rows.${wording}`, { ...values, document })
    : t(`${HISTORY}.rows.${wording}NoSource`, values)
}
